import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import FluentErrorCircle20Regular from '~icons/fluent/error-circle-20-regular'
import { UIButton } from 'src/components/ui/Button'
import { UILink } from 'src/components/ui/Link'
import MarkdownRenderer from 'src/components/ui/MarkdownRenderer'
import Text from 'src/components/ui/Text'
import { useLocation } from 'src/utils/router/route'
import { MioExpression, MioImage } from './Common/MioImage'

interface AppErrorStateProps {
  errorDetail: string
  fullscreen?: boolean
}

export default function AppErrorState({
  errorDetail,
  fullscreen = false,
}: AppErrorStateProps) {
  const { t } = useTranslation(['common'])
  const location = useLocation()

  return (
    <div
      className={
        fullscreen
          ? 'min-h-screen grid place-content-center'
          : 'min-h-[70vh] grid place-content-center'
      }
    >
      <div className="max-w-screen-msm flex flex-col items-center gap-8 p-8">
        <ErrorBoundary fallback={<p>Something wrong</p>}>
          <MioImage
            expression={MioExpression.Cry}
            className="size-[200px] msm:size-[300px]"
          />
          <MarkdownRenderer className="prose prose-sm prose-neutral dark:prose-invert">
            {t('common:app-crashed')}
          </MarkdownRenderer>
          <div className="flex gap-2">
            {location.pathname !== '/' && (
              <UILink href="/" variant="outlined" className="rounded-md">
                <Text i18nKey="common:homepage" />
              </UILink>
            )}
            <UIButton
              variant="outlined"
              onPress={() => window.location.reload()}
            >
              <Text i18nKey="common:reload" />
            </UIButton>
          </div>
        </ErrorBoundary>
        <div className="max-w-full flex items-start gap-2 p-4 text-xs bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-lg">
          <FluentErrorCircle20Regular className="flex-none -my-0.5 -ml-1 inline-block size-5 text-neutral-500" />
          <p className="flex-1 font-mono">{errorDetail}</p>
        </div>
      </div>
    </div>
  )
}
